// apps/web/src/lib/estoque/saldos.ts — Fase 13, Plano 07 (EST-08, T-13-07C).
// Lógica pura da tela de Saldos do estoque próprio: filtros (URL e query da API), estado da lista e colunas por largura,
// mais a apresentação do tipo de movimento no histórico (rótulo, ícone e paleta fechada do UI-SPEC).
// Quem filtra, ordena e pagina é o servidor (`GET /api/v1/estoque/saldos/proprio`); nada aqui recalcula saldo.
import { TIPOS_MOVIMENTO_ESTOQUE, type TipoMovimentoEstoque } from '@pieltec/contracts';

export type TipoFiltroSaldos = 'TODOS' | 'TINTA' | 'OUTRAS';

export interface FiltrosSaldos {
  busca: string;
  /** UUID da localização; vazio = todas. */
  localizacaoId: string;
  tipo: TipoFiltroSaldos;
  mostrarSemSaldo: boolean;
}

/** Tamanho de página da tela (a API aceita até 100). */
export const TAMANHO_PAGINA_SALDOS = 50;
/** Acima deste total de linhas carregadas a grade renderiza só as visíveis (ADR-005 D1). */
export const LIMITE_VIRTUALIZACAO_SALDOS = 200;
/** Largura mínima (px) para Valor e Preferencial ficarem na grade; abaixo disso vão para a linha expandida. */
export const LARGURA_COLUNAS_COMPLETAS = 1024;

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const LIMITE_BUSCA = 100;

export function filtrosSaldosVazios(): FiltrosSaldos {
  return { busca: '', localizacaoId: '', tipo: 'TODOS', mostrarSemSaldo: false };
}

/** Há algum filtro além do padrão (sem busca, todas as localizações, todos os tipos, só itens com saldo)? */
export function filtrosSaldosAtivos(filtros: FiltrosSaldos): boolean {
  return filtros.busca.trim() !== '' || filtros.localizacaoId !== '' || filtros.tipo !== 'TODOS' || filtros.mostrarSemSaldo;
}

interface OpcoesQuerySaldos {
  pagina?: number;
  tamanhoPagina?: number;
  /** Saldo por local de um único material (linha expandida). */
  materialId?: string;
}

/**
 * Query string (sem `?`) com só os filtros ativos, na ordem fixa dos contratos. Usa `URLSearchParams`: nenhum valor do
 * usuário é concatenado na mão (T-13-07C). `tamanhoPagina` padrão 50.
 */
export function montarQuerySaldosProprios(filtros: FiltrosSaldos, opcoes: OpcoesQuerySaldos = {}): string {
  const params = new URLSearchParams();
  params.set('pagina', String(opcoes.pagina ?? 1));
  params.set('tamanhoPagina', String(opcoes.tamanhoPagina ?? TAMANHO_PAGINA_SALDOS));
  const busca = filtros.busca.trim();
  if (busca !== '') params.set('busca', busca.slice(0, LIMITE_BUSCA));
  if (opcoes.materialId) params.set('materialId', opcoes.materialId);
  if (filtros.localizacaoId !== '') params.set('localizacaoId', filtros.localizacaoId);
  if (filtros.tipo !== 'TODOS') params.set('tipo', filtros.tipo);
  if (filtros.mostrarSemSaldo) params.set('mostrarSemSaldo', 'true');
  return params.toString();
}

/**
 * Filtros a partir de `location.search`. Valores inválidos (localização que não é UUID, tipo desconhecido) voltam ao
 * padrão em vez de gerar um 400 da API.
 */
export function lerFiltrosDaUrl(search: string): FiltrosSaldos {
  const params = new URLSearchParams(search);
  const filtros = filtrosSaldosVazios();
  filtros.busca = (params.get('busca') ?? '').trim().slice(0, LIMITE_BUSCA);
  const localizacao = params.get('localizacao') ?? '';
  filtros.localizacaoId = UUID.test(localizacao) ? localizacao : '';
  const tipo = params.get('tipo');
  filtros.tipo = tipo === 'TINTA' || tipo === 'OUTRAS' ? tipo : 'TODOS';
  filtros.mostrarSemSaldo = params.get('semSaldo') === '1';
  return filtros;
}

/** `?busca=...` para `history.replaceState`; string vazia quando não há filtro (URL limpa). */
export function escreverFiltrosNaUrl(filtros: FiltrosSaldos): string {
  const params = new URLSearchParams();
  const busca = filtros.busca.trim();
  if (busca !== '') params.set('busca', busca);
  if (filtros.localizacaoId !== '') params.set('localizacao', filtros.localizacaoId);
  if (filtros.tipo !== 'TODOS') params.set('tipo', filtros.tipo);
  if (filtros.mostrarSemSaldo) params.set('semSaldo', '1');
  const texto = params.toString();
  return texto === '' ? '' : `?${texto}`;
}

export type EstadoDaLista = 'carregando' | 'erro' | 'vazio-sem-saldo' | 'vazio-filtro' | 'dados';

/** Qual das cinco telas mostrar: carga tem prioridade, depois erro, depois os dois vazios, senão a grade. */
export function estadoDaLista(entrada: {
  carregando: boolean;
  erro: boolean;
  itens: readonly unknown[] | null | undefined;
  filtrosAtivos: boolean;
}): EstadoDaLista {
  if (entrada.carregando) return 'carregando';
  if (entrada.erro) return 'erro';
  if (!entrada.itens || entrada.itens.length === 0) return entrada.filtrosAtivos ? 'vazio-filtro' : 'vazio-sem-saldo';
  return 'dados';
}

export interface ColunasVisiveis {
  valor: boolean;
  preferencial: boolean;
}

/** Valor e Preferencial só ficam na grade a partir de 1024px; abaixo vão para a linha expandida (sem rolagem lateral). */
export function colunasVisiveis(larguraPx: number): ColunasVisiveis {
  const completas = larguraPx >= LARGURA_COLUNAS_COMPLETAS;
  return { valor: completas, preferencial: completas };
}

// ---------------------------------------------------------------------------------------------------------------------
// Histórico do item: apresentação do tipo de movimento (paleta "Tipo de movimento" do 13-UI-SPEC)
// ---------------------------------------------------------------------------------------------------------------------

/** Tipos na ordem do filtro "Tipo" do histórico. */
export const TIPOS_FILTRO_HISTORICO: readonly TipoMovimentoEstoque[] = TIPOS_MOVIMENTO_ESTOQUE;

const ROTULOS_TIPO: Readonly<Record<TipoMovimentoEstoque, string>> = {
  ENTRADA_COMPRA: 'Entrada',
  SALDO_ABERTURA: 'Saldo de abertura',
  AJUSTE_INVENTARIO: 'Ajuste de inventário',
  SAIDA_TINTA: 'Saída de tinta',
  DEVOLUCAO_TINTA: 'Devolução de tinta',
  PERDA: 'Perda',
  CONSUMIVEL_SETOR: 'Consumível ao setor',
  TRANSFERENCIA: 'Transferência',
  ESTORNO: 'Estorno',
};

/**
 * Rótulo do tipo. A transferência aparece em duas linhas no histórico (uma por local); o sentido diz qual é a da
 * origem (saída) e qual é a do destino (entrada).
 */
export function rotuloTipoMovimento(tipo: TipoMovimentoEstoque, sentido: 1 | -1): string {
  if (tipo === 'TRANSFERENCIA') return sentido === 1 ? 'Transferência (entrada)' : 'Transferência (saída)';
  return ROTULOS_TIPO[tipo];
}

/** Nome do ícone lucide do tipo (a página mapeia o nome para o componente). */
export type IconeTipoMovimento =
  | 'ArrowDownToLine'
  | 'Flag'
  | 'ArrowUpFromLine'
  | 'ArrowLeftRight'
  | 'AlertTriangle'
  | 'Undo2'
  | 'ClipboardCheck';

const ICONES_TIPO: Readonly<Record<TipoMovimentoEstoque, IconeTipoMovimento>> = {
  ENTRADA_COMPRA: 'ArrowDownToLine',
  SALDO_ABERTURA: 'Flag',
  AJUSTE_INVENTARIO: 'ClipboardCheck',
  SAIDA_TINTA: 'ArrowUpFromLine',
  DEVOLUCAO_TINTA: 'ArrowDownToLine',
  PERDA: 'AlertTriangle',
  CONSUMIVEL_SETOR: 'ArrowUpFromLine',
  TRANSFERENCIA: 'ArrowLeftRight',
  ESTORNO: 'Undo2',
};

export function iconeTipoMovimento(tipo: TipoMovimentoEstoque): IconeTipoMovimento {
  return ICONES_TIPO[tipo];
}

const EMERALD = 'bg-emerald-50 text-emerald-700 border-emerald-200';
const EMERALD_CONTORNO = 'bg-white text-emerald-700 border-emerald-300';
const SLATE = 'bg-slate-50 text-slate-700 border-slate-200';
const BLUE = 'bg-blue-50 text-blue-700 border-blue-200';
const AMBER = 'bg-amber-50 text-amber-700 border-amber-200';

const CLASSES_TIPO: Readonly<Record<TipoMovimentoEstoque, string>> = {
  ENTRADA_COMPRA: EMERALD,
  SALDO_ABERTURA: EMERALD_CONTORNO,
  AJUSTE_INVENTARIO: SLATE,
  SAIDA_TINTA: SLATE,
  DEVOLUCAO_TINTA: EMERALD_CONTORNO,
  PERDA: AMBER,
  CONSUMIVEL_SETOR: SLATE,
  TRANSFERENCIA: BLUE,
  ESTORNO: AMBER,
};

/** Classes do badge do tipo (paleta fechada: cor nunca é o único sinal, o badge sempre tem ícone e texto). */
export function classesTipoMovimento(tipo: TipoMovimentoEstoque): string {
  return CLASSES_TIPO[tipo];
}

/** Sinal explícito da quantidade (`+` entrada, `−` saída), para não depender só de cor ou de seta. */
export function sinalDoMovimento(sentido: 1 | -1): '+' | '−' {
  return sentido === 1 ? '+' : '−';
}

/** Motivos acima deste tamanho ganham "Ver mais" (aproximação do `line-clamp-2` em uma coluna estreita). */
export const LIMITE_MOTIVO_CURTO = 90;

export function motivoPrecisaVerMais(texto: string): boolean {
  return texto.length > LIMITE_MOTIVO_CURTO || texto.includes('\n');
}

/** Período coerente? Vazio de um lado é válido; `de` depois de `ate` (AAAA-MM-DD) não é. */
export function periodoValido(de: string, ate: string): boolean {
  return de === '' || ate === '' || de <= ate;
}

export interface FiltrosHistorico {
  /** AAAA-MM-DD (America/Sao_Paulo); vazio = sem limite. */
  de: string;
  ate: string;
  /** Vazio = todos os tipos. */
  tipo: TipoMovimentoEstoque | '';
  localizacaoId: string;
}

export function filtrosHistoricoVazios(): FiltrosHistorico {
  return { de: '', ate: '', tipo: '', localizacaoId: '' };
}

const DATA_ISO_CURTA = /^\d{4}-\d{2}-\d{2}$/;

/** Query string (sem `?`) do histórico do material; só os filtros ativos e válidos, montada com `URLSearchParams`. */
export function montarQueryHistorico(filtros: FiltrosHistorico, paginacao: { pagina: number; tamanhoPagina: number }): string {
  const params = new URLSearchParams();
  params.set('pagina', String(paginacao.pagina));
  params.set('tamanhoPagina', String(paginacao.tamanhoPagina));
  if (DATA_ISO_CURTA.test(filtros.de)) params.set('de', filtros.de);
  if (DATA_ISO_CURTA.test(filtros.ate)) params.set('ate', filtros.ate);
  if (filtros.tipo !== '' && (TIPOS_MOVIMENTO_ESTOQUE as readonly string[]).includes(filtros.tipo)) params.set('tipo', filtros.tipo);
  if (UUID.test(filtros.localizacaoId)) params.set('localizacaoId', filtros.localizacaoId);
  return params.toString();
}
